import {
  type Product,
  type ProductLookups,
} from '@airship-platform/airship-proposal';
import { type RxCollection, type RxCollectionCreator } from 'rxdb';
import { identityMigration } from '../../rxdb/utils/collectionDefaults';

const pricebookItemsSchema = {
  version: 1,
  primaryKey: 'pricebookId',
  type: 'object',
  properties: {
    pricebookId: { type: 'string', maxLength: 36 },
    name: { type: 'string' },
    type: { type: 'string', maxLength: 20 },
    code: { type: 'string' },
    description: { type: 'string' },
    model: { type: 'string' },
    brand: { type: 'string' },
    defaultPriceType: { type: 'string' },
    prices: { type: 'array' },
    matchesCertifications: { type: 'boolean' },
    equipmentTypeIds: { type: 'array' },
    categoryIds: { type: 'array' },
    capacity: { type: 'array' },
    efficiency: { type: 'array' },
    linkedItems: { type: 'array' },
    images: { type: 'array' },
    warranties: { type: 'array' },
  },
  indexes: [['type']],
  required: ['pricebookId', 'type'],
} as const;

const pricebookCategoriesSchema = {
  version: 1,
  primaryKey: 'categoryId',
  type: 'object',
  properties: {
    categoryId: { type: 'string', maxLength: 36 },
    parentCategoryId: { type: 'string' },
    name: { type: 'string' },
    position: { type: 'number' },
    categoryType: { type: 'string' },
    image: { type: 'string' },
    level: { type: 'number' },
    businessUnitIds: { type: 'array' },
  },
  required: ['categoryId'],
} as const;

const pricebookBrandsSchema = {
  version: 0,
  primaryKey: 'name',
  type: 'object',
  properties: {
    name: { type: 'string', maxLength: 100 },
    count: { type: 'number' },
  },
  required: ['name', 'count'],
} as const;

const pricebookLookupsSchema = {
  version: 1,
  primaryKey: 'discountsAndFeesId',
  type: 'object',
  properties: {
    discountsAndFeesId: { type: 'string', maxLength: 36 },
    displayName: { type: 'string' },
    description: { type: 'string' },
    amountType: { type: 'string' },
    amount: { type: 'number' },
    limit: { type: 'number' },
    taxable: { type: 'boolean' },
    categoryIds: { type: 'array' },
    modifierType: { type: 'string' },
    roundingRule: { type: 'object' },
    taxBasis: { type: 'string' },
    images: { type: 'array' },
    qualifyingPricebookCategoryIds: { type: 'array' },
    qualifyingPricebookIds: { type: 'array' },
  },
  required: ['discountsAndFeesId'],
} as const;

export const pricebookCollections: Record<string, RxCollectionCreator> = {
  pricebook_items: {
    autoMigrate: false,
    schema: pricebookItemsSchema,
    migrationStrategies: { 1: identityMigration },
  },
  pricebook_categories: {
    autoMigrate: false,
    schema: pricebookCategoriesSchema,
    migrationStrategies: { 1: identityMigration },
  },
  pricebook_brands: {
    autoMigrate: false,
    schema: pricebookBrandsSchema,
  },
  pricebook_discounts: {
    autoMigrate: false,
    schema: pricebookLookupsSchema,
    migrationStrategies: { 1: identityMigration },
  },
  pricebook_fees: {
    autoMigrate: false,
    schema: pricebookLookupsSchema,
    migrationStrategies: { 1: identityMigration },
  },
  pricebook_rebates: {
    autoMigrate: false,
    schema: pricebookLookupsSchema,
    migrationStrategies: { 1: identityMigration },
  },
};

export type PricebookCollections = {
  pricebook_items: RxCollection<Product>;
  pricebook_categories: RxCollection<ProductLookups['categories'][number]>;
  pricebook_brands: RxCollection<ProductLookups['brands'][number]>;
  pricebook_discounts: RxCollection<ProductLookups['discounts'][number]>;
  pricebook_fees: RxCollection<ProductLookups['fees'][number]>;
  pricebook_rebates: RxCollection<ProductLookups['rebates'][number]>;
};
